/**
 * @file src/_modulos/prenota/tabela/layouts/config/store/cache.ts
 * @description Cache simplificado para tabela de pré-notas
 * 
 * Princípios aplicados:
 * - KISS: Cache simples em memória com persistência básica
 * - SRP: Apenas gerencia cache, não estado de UI
 * - YAGNI: Remove funcionalidades complexas não utilizadas
 */

/* -------------------------------------------------------------------------- */
/* TIPOS                                                                      */
/* -------------------------------------------------------------------------- */

export interface CacheEntry {
  key: string;
  data: any[];
  timestamp: number;
  totalCount: number;
  hasMore: boolean;
  page: number;
  pageSize: number;
  filters: any;
}

export interface CacheOptions {
  totalCount: number;
  hasMore: boolean;
  page: number;
  pageSize: number;
  filters: any;
}

/* -------------------------------------------------------------------------- */
/* CACHE SIMPLES                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Cache simples em memória com persistência localStorage
 * 
 * Estratégia: Memória para velocidade, localStorage para persistência
 */
class SimpleCache {
  private memoryCache = new Map<string, CacheEntry>();
  private readonly STORAGE_KEY = "prenota-table-cache";

  /**
   * Salva dados no cache
   */
  setCache(key: string, data: any[], options: CacheOptions): void {
    const entry: CacheEntry = {
      key,
      data,
      timestamp: Date.now(),
      totalCount: options.totalCount,
      hasMore: options.hasMore,
      page: options.page,
      pageSize: options.pageSize,
      filters: options.filters,
    };

    // Salva em memória
    this.memoryCache.set(key, entry);

    // Salva no localStorage
    this.persistToStorage();
  }

  /**
   * Obtém dados do cache
   */
  getCache(key: string): CacheEntry | null {
    // Tenta memória primeiro
    const memoryEntry = this.memoryCache.get(key);
    if (memoryEntry) {
      return memoryEntry;
    }

    // Se não encontrou, tenta localStorage
    const storageEntry = this.getFromStorage(key);
    if (storageEntry) {
      // Restaura em memória
      this.memoryCache.set(key, storageEntry);
      return storageEntry;
    }

    return null;
  }

  /**
   * Verifica se o cache está válido
   */
  isCacheValid(key: string, maxAgeMinutes: number = 5): boolean {
    const entry = this.getCache(key);
    if (!entry) return false;

    const ageMinutes = (Date.now() - entry.timestamp) / (1000 * 60);
    return ageMinutes < maxAgeMinutes;
  }

  /**
   * Limpa cache de uma tab específica
   */
  clearCache(key: string): void {
    this.memoryCache.delete(key);
    this.persistToStorage();
  }

  /**
   * Limpa todo o cache
   */
  clearAllCache(): void {
    this.memoryCache.clear();
    localStorage.removeItem(this.STORAGE_KEY);
  }

  /**
   * Obtém dados antigos para mostrar imediatamente
   */
  getStaleData(key: string): any[] | null {
    const entry = this.getCache(key);
    return entry ? entry.data : null;
  }

  /**
   * Verifica se há dados antigos disponíveis
   */
  hasStaleData(key: string): boolean {
    return this.memoryCache.has(key) || this.getFromStorage(key) !== null;
  }

  /* ------------------------------------------------------------------------ */
  /* MÉTODOS PRIVADOS                                                         */
  /* ------------------------------------------------------------------------ */

  /**
   * Persiste cache para localStorage
   */
  private persistToStorage(): void {
    try {
      const cacheData = Object.fromEntries(this.memoryCache);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.warn("⚠️ [SimpleCache] Erro ao persistir cache:", error);
    }
  }

  /**
   * Obtém dados do localStorage
   */
  private getFromStorage(key: string): CacheEntry | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return null;

      const cacheData = JSON.parse(stored);
      return cacheData[key] || null;
    } catch (error) {
      console.warn("⚠️ [SimpleCache] Erro ao ler cache:", error);
      return null;
    }
  }

  /**
   * Restaura todo o cache do localStorage
   */
  private restoreFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return;

      const cacheData = JSON.parse(stored);
      this.memoryCache = new Map(Object.entries(cacheData));
    } catch (error) {
      console.warn("⚠️ [SimpleCache] Erro ao restaurar cache:", error);
    }
  }
}

/* -------------------------------------------------------------------------- */
/* INSTÂNCIA SINGLETON                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Instância singleton do cache
 */
export const tableCache = new SimpleCache();

// Restaura cache na inicialização
if (typeof window !== "undefined") {
  tableCache["restoreFromStorage"]();
} 